import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Form, Pagination } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Apiconnect from "../services/Apiconnect.js";

const styles = {
  addButton: {
    backgroundColor: '#D1D2D2',
    color: 'black',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '15px',
    width: '142px',
    height: '35px',
  },
  searchInput: {
    width: '250px',
    marginRight: '10px',
  },
  searchButton: {
    marginRight: '10px',
  },
};

const Packages = () => {
  const navigate = useNavigate();
  const [packageList, setPackageList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const itemsPerPage = 5;

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError("Authentication token not found. Please login again.");
        toast.error("Authentication token not found. Please login again.");
        setIsLoading(false);
        return;
      }

      const response = await Apiconnect.getData("packages");
      
      if (response.data && response.data.status === 'success') {
        setPackageList(response.data.data || []);
        setFilteredPackages(response.data.data || []);
      } else {
        const errorMessage = response.data?.message || 'Failed to fetch package list';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
      setError('An error occurred while fetching packages');
      toast.error('An error occurred while fetching packages');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    const filtered = packageList.filter((pkg) =>
      pkg.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPackages(filtered);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchTerm('');
    setFilteredPackages(packageList);
    setCurrentPage(1);
  };

  const handleEdit = (id) => {
    navigate(`/admin/package/edit/${id}`);
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPackages.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPackages.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="container-fluid">
      <Card className="shadow">
        <Card.Header className="bg-primary text-white">
          <div className="d-flex justify-content-between align-items-center">
            <h4 className="mb-0" style={{ fontSize: '20px', color: 'white' }}>List of Packages</h4>
            <Link to="/admin/package/add" style={styles.addButton}>
              + Add Package
            </Link>
          </div>
        </Card.Header>

        <div className="d-flex mb-3 mt-3 ml-3 px-3">
          <Form.Control
            type="text"
            placeholder="Search by package name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <Button variant="primary" onClick={handleSearch} style={styles.searchButton}>
            Search
          </Button>
          <Button variant="secondary" onClick={handleReset}>
            Reset
          </Button>
        </div>

        <Card.Body>
          {isLoading ? (
            <div className="text-center">
              <p>Loading packages...</p>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          ) : filteredPackages.length === 0 ? (
            <div className="alert alert-info" role="alert">
              No packages found.
            </div>
          ) : (
            <div className="table-responsive">
              <Table striped bordered hover className="mb-0" style={{ fontSize: '14px' }}>
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Name</th>                     
                    <th>Distribution Price</th>
                    <th>Price</th>
                    <th>Validity</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map((pkg, index) => (
                    <tr key={pkg.id} style={{ lineHeight: '1.2' }}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>{pkg.name}</td>                     
                      <td>₹{pkg.distribution_price}</td>
                      <td>₹{pkg.price}</td>
                      <td>{pkg.pack_validity} Days</td>
                      <td>
                        <Button 
                          variant="dark" 
                          size="sm" 
                          style={{ padding: '2px 8px', fontSize: '12px' }}
                          onClick={() => handleEdit(pkg.id)}
                        >
                          ✏️ Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>

        <Card.Footer>
          {!isLoading && filteredPackages.length > 0 && (
            <Pagination className="justify-content-center">
              <Pagination.Prev onClick={handlePrevious} disabled={currentPage === 1}>
                <FaChevronLeft />
              </Pagination.Prev>
              {[...Array(totalPages).keys()].map((page) => (
                <Pagination.Item
                  key={page + 1}
                  active={page + 1 === currentPage}
                  onClick={() => handlePageChange(page + 1)}
                >
                  {page + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next onClick={handleNext} disabled={currentPage === totalPages}>
                <FaChevronRight />
              </Pagination.Next>
            </Pagination>
          )}
        </Card.Footer>
      </Card>
      <ToastContainer />
    </div>
  );
};

export default Packages;